<template>
  <div class="burc-container">
    <div class="mx-auto w-full mt-4 max-w-[1100px] min-h-screen pb-12">
      <!-- masthead -->
      <AdController unit="masthead" />

      <!-- filter -->
      <div class="w-full mt-6 space-y-8 px-4 sm:px-0">
        <ArticleBreadcrumb />

        <!-- article title -->
        <h1 v-if="horoscope === 'all'" class="text-24 sm:text-28 font-bold text-white tracking-tight leading-snug">
          {{ article.title }}
        </h1>
        <AstrologyArticleTitle v-else :horoscope="horoscope" :date="date" :type="type" :frequency="frequency" />

        <AstrologyTypeSelect :horoscope="horoscope" :date="date" :type="type" :frequency="frequency" />
        <AstrologyDaySelect
          v-if="frequency === 'daily' || horoscope === 'all'"
          :horoscope="horoscope"
          :date="date"
          :type="type"
          :frequency="frequency"
        />
        <AstrologyArticleAuthor :authors="article.authors" />
      </div>

      <!-- content -->
      <div class="w-full flex mt-6">
        <!-- body -->
        <div class="text-white w-full">
          <OArticle :item="article" :hide="['meta', 'title', 'breadcrumb', 'tags', 'kunye', 'author']" />
          <!-- Frequency Widget Cards -->
          <div v-if="!isMobile && horoscope !== 'all'" class="flex flex-col mt-12">
            <div class="text-24 leading-32 font-bold mb-3">Daha uzun vadeli bir astrolojik bakış ister misin?</div>
            <div class="text-md leading-20 font-normal mb-6">{{ year }} Seni Neler Bekliyor? Burç Yorumlarını Oku!</div>
            <div class="flex flex-row flex-wrap gap-4">
              <AstrologyWidgetCard
                v-for="(card, index) in availableFrequencies"
                :key="index"
                :card="getFrequencyDetails(card, horoscope)"
                :class="{ 'w-full': index === availableFrequencies.length - 1, 'flex-1': index !== availableFrequencies.length - 1 }"
              />
            </div>
          </div>
        </div>
        <!-- aside -->
        <aside class="text-white hidden sm:block sm:w-[344px] sm:ml-9">
          <!-- popular astrology cards -->
          <AdController unit="newSidebarSponTop" class="bg-[#f7f8fb] p-6 sticky z-1 top-24 xmt-8" />
          <AstrologyPopularCard v-if="false" />
          <!-- other horoscope cards -->
          <div class="sticky z-1 top-24 pt-4 xtop-[420px] bg-black">
            <div class="text-28 leading-36 font-bold mb-4">Diğer Burçlar</div>
            <AstrologyCardSwiper :type="type" :date="date" :frequency="frequency" />
          </div>
        </aside>
      </div>

      <!-- mobile bottom widgets -->
      <div v-if="isMobile" class="flex flex-col justify-center flex-wrap gap-4 w-full">
        <!-- other horoscope cards -->
        <div class="flex flex-col gap-4 w-full px-4">
          <div class="text-white text-28 leading-36 font-bold">Diğer Burçlar</div>
          <AstrologyCardSwiper :type="type" :date="date" :frequency="frequency" />
        </div>
        <!-- popular astrology cards -->
        <AstrologyPopularCard v-if="false" xclass="flex flex-col w-full" />
        <!-- cta -->
        <div v-if="horoscope !== 'all'" class="w-full bg-[#080C25] px-4">
          <AstrologyCTA :horoscope-name="horoscopeName" :horoscope="horoscope" :type="variant" :date="date" :frequency="frequency" />
        </div>
        <!-- frequency widget cards -->
        <div v-if="horoscope !== 'all'" class="flex flex-col mt-12 px-4">
          <div class="text-24 text-white leading-32 font-bold mb-3">Daha uzun vadeli bir astrolojik bakış ister misin?</div>
          <div class="text-md text-white leading-20 font-normal mb-6">{{ year }} Seni Neler Bekliyor? Burç Yorumlarını Oku!</div>
          <div class="flex flex-col flex-wrap gap-4">
            <AstrologyWidgetCard v-for="(card, index) in availableFrequencies" :key="index" :card="getFrequencyDetails(card, horoscope)" />
          </div>
        </div>
      </div>
    </div>

    <!-- cta -->
    <div v-if="!isMobile && horoscope !== 'all'" class="h-[400px] w-full bg-[#080C25] u-center py-16">
      <div class="mx-auto w-full max-w-[1100px] u-center">
        <AstrologyCTA :horoscope-name="horoscopeName" :horoscope="horoscope" :type="variant" :date="date" :frequency="frequency" />
      </div>
    </div>

    <!-- kunye -->
    <div class="h-[176px] sm:h-52 w-full bg-black u-center px-4 sm:px-0">
      <div class="mx-auto w-full max-w-[1100px] u-center">
        <AstrologyArticleAuthor :authors="article.authors" is-kunye />
      </div>
    </div>
    <!-- reactions and comments -->
    <div class="w-full bg-[#101116] u-center px-4 sm:px-0">
      <div class="mx-auto w-full max-w-[1100px]">
        <LazyHydrate :when-visible="{ rootMargin: '100px' }">
          <OArticleReactions class="mt-10 mb-6" :item="article"></OArticleReactions>
        </LazyHydrate>
        <LazyHydrate :when-visible="{ rootMargin: '300px' }">
          <OArticleComments :id="'reactions-' + article.legacyId" class="mb-10" :item="article"></OArticleComments>
        </LazyHydrate>
      </div>
    </div>
  </div>
</template>

<script>
import { defineComponent } from '@nuxtjs/composition-api';
import OArticle from '@/modules/article/components/OArticle.vue';
import ArticleBreadcrumb from '@/modules/article/components/ArticleBreadcrumb';
import LazyHydrate from 'vue-lazy-hydration';
import AdController from '@/modules/ads/components/AdController';
import { useDevice } from '@/composables/useDevice';
import OArticleReactions from '~/modules/article/components/OArticleReactions.vue';
import OArticleComments from '~/modules/article/components/OArticleComments.vue';
import AstrologyPopularCard from '~/modules/astrology/components/AstrologyPopularCard.vue';
import AstrologyArticleTitle from '~/modules/astrology/components/AstrologyArticleTitle.vue';
import AstrologyDaySelect from '~/modules/astrology/components/AstrologyDaySelect.vue';
import AstrologyTypeSelect from '~/modules/astrology/components/AstrologyTypeSelect.vue';
import AstrologyArticleAuthor from '~/modules/astrology/components/AstrologyArticleAuthor.vue';
import AstrologyCardSwiper from '~/modules/astrology/components/AstrologyCardSwiper.vue';
import AstrologyWidgetCard from '~/modules/astrology/components/AstrologyWidgetCard.vue';
import AstrologyCTA from '~/modules/astrology/components/AstrologyCTA.vue';
import { getFrequencyDetails, getAvailableFrequencies, getHoroscopeName, getVariant } from '~/modules/astrology/utils/astrologyUtils';

export default defineComponent({
  components: {
    OArticle,
    OArticleReactions,
    OArticleComments,
    ArticleBreadcrumb,
    LazyHydrate,
    AdController,
    AstrologyPopularCard,
    AstrologyCardSwiper,
    AstrologyArticleTitle,
    AstrologyDaySelect,
    AstrologyTypeSelect,
    AstrologyArticleAuthor,
    AstrologyWidgetCard,
    AstrologyCTA,
  },
  props: {
    article: {
      type: Object,
      required: true,
    },
    horoscope: {
      type: String,
      required: true,
    },
    date: {
      type: String,
      required: true,
    },
    type: {
      type: String,
      required: true,
    },
    frequency: {
      type: String,
      required: true,
    },
  },
  setup(props) {
    const { isMobile } = useDevice();
    const availableFrequencies = getAvailableFrequencies(props.frequency);
    const horoscopeName = getHoroscopeName(props.horoscope);
    const variant = getVariant(props.type);
    const year = new Date().getFullYear();

    return {
      availableFrequencies,
      horoscopeName,
      variant,
      getFrequencyDetails,
      isMobile,
      year,
    };
  },
});
</script>

<style lang="scss">
@import '~/assets/css/custom-article.scss';
.burc-container {
  background: linear-gradient(180deg, #2e2270 0%, #000 8.38%);
}
</style>
